import { useState, useEffect, useCallback } from "react";
import { adminLogin as apiAdminLogin, adminApiCall } from "@/lib/api";

interface Admin {
  id: number;
  username: string;
  name: string;
  role: string;
  permissions: string[];
  user_type?: string;
  must_change_password?: boolean;
  client_sub_permissions?: Record<string, boolean>;
  progress_sub_permissions?: Record<string, boolean>;
  finance_sub_permissions?: Record<string, boolean>;
  system_sub_permissions?: Record<string, boolean>;
  data_scope?: "global" | "personal" | null;
}

export interface AdminClient {
  id: number;
  name: string | null;
  phone: string;
  client_code?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  advisor_name: string | null;
  advisor_phone: string | null;
  advisor_employee_name?: string | null;
  advisor_employee_id?: number | null;
  id_number?: string | null;
  passport_number?: string | null;
  wechat?: string | null;
  is_single?: boolean | null;
  partner_name?: string | null;
  partner_id_number?: string | null;
  partner_passport?: string | null;
  partner_phone?: string | null;
  partner_wechat?: string | null;
  country?: string | null;
  service_plan?: string | null;
  service_sub_plan?: string | null;
  joint_stimulation?: boolean | null;
  notes?: string | null;
  current_stage_name?: string | null;
  current_stage_status?: string | null;
  current_stage_id?: number | null;
  genetic_material_types?: string[];
  genetic_alert?: boolean;
  genetic_alert_details?: Array<{ material_type: string; end_date: string; current_status: string }>;
}

export interface Stage {
  id: number;
  name: string;
  description: string | null;
  sort_order: number;
  icon: string | null;
  service_plan?: string | null;
  has_fee?: boolean | null;
  is_income?: boolean | null;
  is_expense?: boolean | null;
}

export interface AdminProgress {
  id: number;
  client_id: number;
  stage_id: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  fee_receivable?: number | null;
  fee_received?: number | null;
  media: Array<{ id?: number; url: string; type: string; title: string }>;
  documents: Array<{ id?: number; url: string; type: string; title: string }>;
  stage?: { name: string; sort_order: number };
}

export interface TrendItem {
  date: string;
  newClients: number;
  activeClients: number;
}

export interface AdminStats {
  totalClients: number;
  activeClients: number;
  completedClients: number;
  trend?: TrendItem[];
}

export interface Role {
  id: number;
  name: string;
  permissions: string[];
  is_default: boolean;
  created_at: string;
  client_sub_permissions?: Record<string, boolean>;
  progress_sub_permissions?: Record<string, boolean>;
  finance_sub_permissions?: Record<string, boolean>;
  system_sub_permissions?: Record<string, boolean>;
  data_scope?: "global" | "personal";
}

export interface Employee {
  id: number;
  name: string;
  phone: string | null;
  username: string;
  role_id: number | null;
  notes: string | null;
  status: string;
  is_super_admin: boolean;
  created_at: string;
  roles?: { name: string; permissions: string[]; client_sub_permissions?: Record<string, boolean>; progress_sub_permissions?: Record<string, boolean>; finance_sub_permissions?: Record<string, boolean>; system_sub_permissions?: Record<string, boolean>; data_scope?: "global" | "personal" } | null;
}

export interface ClientTag {
  id: number;
  name: string;
  color: string;
  created_at: string;
}

export interface ScheduledNotification {
  id: number;
  title: string;
  content: string;
  type: string;
  client_id: number;
  client?: { name: string | null; phone: string } | null;
  scheduled_at: string;
  batch_id: string | null;
  is_sent: boolean;
}

export interface ScheduledGroup {
  batch_id: string | null;
  title: string;
  content: string;
  type: string;
  scheduled_at: string;
  sender_name: string | null;
  sender_employee_id: number | null;
  client_count: number;
  ids: number[];
}

export function useAdminAuth() {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem("admin_token");
    const storedAdmin = localStorage.getItem("admin_user");
    if (storedToken && storedAdmin) {
      setToken(storedToken);
      try {
        setAdmin(JSON.parse(storedAdmin));
      } catch {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
      }
    }
    setLoading(false);
  }, []);

  const isSuperAdmin = useCallback(() => {
    return (
      admin?.role === "super_admin" ||
      admin?.role === "超级管理员" ||
      admin?.user_type === "admin" ||
      admin?.permissions?.includes("all") ||
      false
    );
  }, [admin]);

  const isGlobalView = useCallback(() => {
    if (isSuperAdmin()) return true;
    return admin?.data_scope === "global";
  }, [admin, isSuperAdmin]);

  const hasPermission = useCallback(
    (perm: string) => {
      return (
        admin?.permissions?.includes(perm) ||
        admin?.permissions?.includes("all") ||
        admin?.user_type === "admin" ||
        isSuperAdmin() ||
        false
      );
    },
    [admin, isSuperAdmin]
  );

  const hasClientSubPermission = useCallback(
    (subPerm: string) => {
      if (isSuperAdmin()) return true;
      return admin?.client_sub_permissions?.[subPerm] === true;
    },
    [admin, isSuperAdmin]
  );

  const hasProgressSubPermission = useCallback(
    (subPerm: string) => {
      if (isSuperAdmin()) return true;
      if (admin?.user_type === "admin") return true;
      return admin?.progress_sub_permissions?.[subPerm] === true;
    },
    [admin, isSuperAdmin]
  );

  const hasFinanceSubPermission = useCallback(
    (subPerm: string) => {
      if (isSuperAdmin()) return true;
      if (admin?.user_type === "admin") return true;
      return admin?.finance_sub_permissions?.[subPerm] === true;
    },
    [admin, isSuperAdmin]
  );

  const hasSystemSubPermission = useCallback(
    (subPerm: string) => {
      if (isSuperAdmin()) return true;
      if (admin?.user_type === "admin") return true;
      return admin?.system_sub_permissions?.[subPerm] === true;
    },
    [admin, isSuperAdmin]
  );

  const login = useCallback(async (username: string, password: string) => {
    try {
      const data: any = await apiAdminLogin(username, password);
      if (!data || !data.success) {
        return { success: false, error: data?.error || "登录失败" };
      }
      if (data?.success) {
        localStorage.setItem("admin_token", data.token);
        const adminObj = {
          ...data.admin,
          must_change_password: data.admin?.must_change_password || data.must_change_password || false,
          client_sub_permissions: data.admin?.client_sub_permissions || {},
          progress_sub_permissions: data.admin?.progress_sub_permissions || {},
          finance_sub_permissions: data.admin?.finance_sub_permissions || {},
          system_sub_permissions: data.admin?.system_sub_permissions || {},
          data_scope: data.admin?.data_scope || null,
        };
        setToken(data.token);
        setAdmin(adminObj);
        localStorage.setItem("admin_user", JSON.stringify(adminObj));
        return { success: true };
      }
    } catch {
      return { success: false, error: "网络错误，请稍后重试" };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    setToken(null);
    setAdmin(null);
  }, []);

  const apiCall = useCallback(
    async (action: string, payload?: Record<string, unknown>) => {
      const storedToken = token || localStorage.getItem("admin_token");
      if (!storedToken) return { success: false, error: "\u672a\u767b\u5f55" };
      try {
        // Build body explicitly to prevent payload keys from overriding the action
        const body: Record<string, unknown> = { token: storedToken, action };
        if (payload) {
          for (const [key, value] of Object.entries(payload)) {
            if (key !== "action" && value !== undefined) {
              body[key] = value;
            }
          }
        }
        const data: any = await adminApiCall(action, payload);
        if (data?.error === "\u65e0\u6548\u7684 token" || data?.error === "\u7f3a\u5c11 token" || data?.error === "\u672a\u767b\u5f55") {
          localStorage.removeItem("admin_token");
          localStorage.removeItem("admin_user");
          setToken(null);
          setAdmin(null);
          setTimeout(() => { window.location.href = "/admin/login"; }, 100);
          return { success: false, error: "\u767b\u5f55\u5df2\u8fc7\u671f\uff0c\u8bf7\u91cd\u65b0\u767b\u5f55" };
        }
        return data;
      } catch (err: any) {
        return { success: false, error: err?.message || "\u7f51\u7edc\u9519\u8bef" };
      }
    },
    [token]
  );

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    return apiCall("change_password", { old_password: oldPassword, new_password: newPassword });
  }, [apiCall]);

  const fetchFinanceData = useCallback(async (params?: { country?: string; advisor_id?: string }) => {
    return apiCall("get_finance_data", params);
  }, [apiCall]);

  const fetchFinanceExpenseData = useCallback(async (params?: { country?: string; advisor_id?: string }) => {
    return apiCall("get_finance_expense_data", params);
  }, [apiCall]);

  const fetchFinanceStats = useCallback(async (params?: { country?: string; advisor_id?: string }) => {
    return apiCall("get_finance_stats", params);
  }, [apiCall]);

  const fetchDashboard = useCallback(async (params?: { timeRange?: string }) => {
    return apiCall("get_dashboard", params);
  }, [apiCall]);

  const fetchClients = useCallback(
    async (params?: { search?: string; status?: string; limit?: number; offset?: number; country?: string; service_plan?: string; client_source?: string; is_single?: string; progress_status?: string; advisor_id?: string; }) => {
      return apiCall("get_clients", params);
    },
    [apiCall]
  );

  const fetchClientDetail = useCallback(
    async (clientId: number) => { return apiCall("get_client_detail", { client_id: clientId }); },
    [apiCall]
  );

  const createClient = useCallback(
    async (payload: Record<string, unknown>) => { return apiCall("create_client", payload); },
    [apiCall]
  );

  const updateClient = useCallback(
    async (clientId: number, payload: Record<string, unknown>) => { return apiCall("update_client", { client_id: clientId, ...payload }); },
    [apiCall]
  );

  const deleteClient = useCallback(
    async (clientId: number) => { return apiCall("delete_client", { client_id: clientId }); },
    [apiCall]
  );

  const assignAdvisor = useCallback(
    async (clientId: number, advisorEmployeeId: number) => { return apiCall("assign_advisor", { client_id: clientId, advisor_employee_id: advisorEmployeeId }); },
    [apiCall]
  );

  const updateProgress = useCallback(
    async (payload: { client_id: number; stage_id: number; status?: string; notes?: string; media?: Array<{ url: string; type: string; title: string }>; documents?: Array<{ url: string; type: string; title: string }>; start_now?: boolean; complete_now?: boolean; fee_receivable?: number; fee_received?: number; fee_usd_receivable?: number; fee_usd_received?: number; }) => {
      return apiCall("update_progress", payload);
    },
    [apiCall]
  );

  const sendNotification = useCallback(
    async (payload: { client_id: number; title: string; content: string; type?: string }) => { return apiCall("send_notification", payload); },
    [apiCall]
  );

  const fetchAllStages = useCallback(async () => { return apiCall("get_all_stages"); }, [apiCall]);

  const uploadFile = useCallback(
    async (payload: { file_base64: string; file_name: string; bucket: string; content_type?: string; client_id?: number; stage_id?: number; stage_name?: string }) => { return apiCall("upload_file", payload); },
    [apiCall]
  );

  const fetchRoles = useCallback(async () => { return apiCall("get_roles"); }, [apiCall]);

  const createRole = useCallback(
    async (payload: { name: string; permissions: string[]; is_default?: boolean; client_sub_permissions?: Record<string, boolean>; progress_sub_permissions?: Record<string, boolean>; data_scope?: "global" | "personal" }) => {
      return apiCall("create_role", payload);
    },
    [apiCall]
  );

  const updateRole = useCallback(
    async (roleId: number, payload: { name?: string; permissions?: string[]; is_default?: boolean; client_sub_permissions?: Record<string, boolean>; progress_sub_permissions?: Record<string, boolean>; data_scope?: "global" | "personal" }) => {
      return apiCall("update_role", { role_id: roleId, ...payload });
    },
    [apiCall]
  );

  const deleteRole = useCallback(
    async (roleId: number) => { return apiCall("delete_role", { role_id: roleId }); },
    [apiCall]
  );

  const fetchEmployees = useCallback(
    async (params?: { search?: string; status?: string; limit?: number; offset?: number }) => { return apiCall("get_employees", params); },
    [apiCall]
  );

  const createEmployee = useCallback(
    async (payload: { name: string; phone?: string; username: string; password: string; role_id?: number; notes?: string; status?: string }) => {
      return apiCall("create_employee", payload);
    },
    [apiCall]
  );

  const updateEmployee = useCallback(
    async (employeeId: number, payload: Record<string, unknown>) => { return apiCall("update_employee", { employee_id: employeeId, ...payload }); },
    [apiCall]
  );

  const deleteEmployee = useCallback(
    async (employeeId: number) => { return apiCall("delete_employee", { employee_id: employeeId }); },
    [apiCall]
  );

  const fetchTags = useCallback(async () => { return apiCall("get_tags"); }, [apiCall]);
  const createTag = useCallback(async (payload: { name: string; color?: string }) => { return apiCall("create_tag", payload); }, [apiCall]);
  const updateTag = useCallback(async (tagId: number, payload: { name?: string; color?: string }) => { return apiCall("update_tag", { tag_id: tagId, ...payload }); }, [apiCall]);
  const deleteTag = useCallback(async (tagId: number) => { return apiCall("delete_tag", { tag_id: tagId }); }, [apiCall]);

  const sendBulkNotification = useCallback(
    async (payload: { client_ids: number[]; title: string; content: string; type?: string; scheduled_at?: string | null; }) => {
      return apiCall("send_bulk_notification", payload);
    },
    [apiCall]
  );

  const fetchScheduledNotifications = useCallback(async () => { return apiCall("get_scheduled_notifications"); }, [apiCall]);

  const cancelScheduledNotification = useCallback(
    async (payload: { batch_id?: string; notification_ids?: number[] }) => { return apiCall("cancel_scheduled_notification", payload); },
    [apiCall]
  );

  const sendScheduledNow = useCallback(
    async (payload: { batch_id?: string; notification_ids?: number[] }) => { return apiCall("send_scheduled_now", payload); },
    [apiCall]
  );

  const updateScheduledNotification = useCallback(
    async (payload: { batch_id?: string; notification_ids?: number[]; scheduled_at: string }) => { return apiCall("update_scheduled_notification", payload); },
    [apiCall]
  );

  const fetchNotificationHistory = useCallback(
    async (params?: { limit?: number; offset?: number; sender?: string; client_phone?: string; client_ids?: number[]; type?: string }) => {
      return apiCall("get_notification_history", params);
    },
    [apiCall]
  );

  const fetchOperationLogs = useCallback(
    async (params?: { limit?: number; offset?: number; employee_name?: string; log_action?: string; target_type?: string; module?: string; time_range?: string }) => {
      return apiCall("get_operation_logs", params);
    },
    [apiCall]
  );

  const exportOperationLogs = useCallback(
    async (params?: { employee_name?: string; log_action?: string; target_type?: string; module?: string; time_range?: string }) => {
      return apiCall("export_operation_logs", params);
    },
    [apiCall]
  );

  const fetchRecentFollowup = useCallback(async () => { return apiCall("get_recent_followup"); }, [apiCall]);

  return {
    admin, token, loading, login, logout, changePassword,
    hasPermission, hasClientSubPermission, hasProgressSubPermission, hasFinanceSubPermission, hasSystemSubPermission, isSuperAdmin, isGlobalView,
    fetchFinanceData, fetchFinanceExpenseData, fetchFinanceStats, fetchDashboard, fetchClients, fetchClientDetail,
    createClient, updateClient, deleteClient, assignAdvisor, updateProgress, sendNotification,
    fetchAllStages, uploadFile, fetchRoles, createRole, updateRole, deleteRole,
    fetchEmployees, createEmployee, updateEmployee, deleteEmployee,
    fetchTags, createTag, updateTag, deleteTag,
    sendBulkNotification, fetchScheduledNotifications, cancelScheduledNotification, sendScheduledNow, updateScheduledNotification,
    fetchNotificationHistory, fetchOperationLogs, exportOperationLogs, fetchRecentFollowup,
  };
}